import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";

const portfolioDetails = {
  "go-guest-house-lahore": {
    title: "Go Guest House Lahore",
    type: "Guest House",
    city: "Lahore, Pakistan",
    address: "Airline Society, Near UCP, Lahore",
    description:
      "A Lahore-based guest house where we applied our hospitality management experience to improve property presentation, guest service, and online booking operations.",
    services:
      "Hospitality Management • Online Presence • Property Presentation • Booking Support",
    bookingUrl:
      "https://www.booking.com/hotel/pk/go-guest-house-lahore.html",
    roomTypes: [
      "Deluxe Single Bedroom",
      "Deluxe Twin Bedroom",
      "Deluxe Family Bedroom",
    ],
    cover: "/portfolio/go-guest-house-lahore/cover.jpg",
    gallery: [
      "/portfolio/go-guest-house-lahore/go1.jpg",
      "/portfolio/go-guest-house-lahore/go2.jpg",
      "/portfolio/go-guest-house-lahore/go3.jpg",
      "/portfolio/go-guest-house-lahore/go4.jpg",
      "/portfolio/go-guest-house-lahore/go5.jpg",
      "/portfolio/go-guest-house-lahore/go6.jpg",
      "/portfolio/go-guest-house-lahore/go7.jpg",
    ],
  },

  "second-home-family-hotel": {
    title: "Second Home Family Hotel",
    type: "Family Hotel",
    city: "Lahore, Pakistan",
    address:
      "Behind Shoukat Khanam Hospital, R-III Block, Johar Town, Lahore",
    description:
      "A family hotel supported through HM Services with a focus on professional property presentation, guest service, and a stronger online presence.",
    services:
      "Hospitality Management • Online Presence • Property Support",
    bookingUrl:
      "https://www.booking.com/hotel/pk/second-home-family.html",
    roomTypes: [
      "Budget Single Room",
      "Deluxe Double Room",
      "Deluxe Family Room",
    ],
    cover: "/portfolio/second-home-family-hotel/cover.jpg",
    gallery: [],
  },

  "waves-guest-house-clifton-khi": {
    title: "Waves Guest House Clifton KHI",
    type: "Guest House",
    city: "Karachi, Pakistan",
    address: "Block 4, Clifton, Karachi, near Dolmen Mall",
    description:
      "A luxury guest house in Clifton Block 4, Karachi, located near Dolmen Mall and close to the Chinese Consulate. The property is also just a short drive from the sea, making it a convenient choice for both business and leisure guests. Waves Guest House offers well-furnished rooms, an in-house menu and kitchen, and professional chef services for a comfortable and welcoming stay.",
    services:
      "Hospitality Management • Online Presence • Property Presentation • Booking Support",
    bookingUrl:
      "https://www.booking.com/hotel/pk/waves-guest-house-clifton-khi.html",
    roomTypes: [
      "Budget Single Room",
      "Deluxe Double Room",
      "Deluxe Family Room",
    ],
    cover: "/portfolio/waves-guest-house-clifton-khi/cover.jpg",
    gallery: [],
  },
};

export default function PortfolioProject() {
  const { slug } = useParams();
  const project = portfolioDetails[slug];

  if (!project) {
    return (
      <div className="min-h-screen bg-white px-5 py-32 text-center">
        <h1 className="font-serif text-3xl font-semibold text-navy">
          Project Not Found
        </h1>

        <p className="mt-4 text-slate-600">
          The portfolio project you are looking for does not exist.
        </p>
      </div>
    );
  }

  return (
    <main className="bg-white">
      <section className="bg-navy px-5 py-20 sm:px-8 lg:py-28">
        <div className="mx-auto max-w-6xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-sky hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Our Work
          </Link>

          <div className="mt-10">
            <span className="rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-sky">
              {project.type}
            </span>

            <h1 className="mt-5 font-serif text-4xl font-semibold text-white sm:text-5xl">
              {project.title}
            </h1>

            <p className="mt-4 flex items-center gap-2 text-slate-300">
              <MapPin className="h-4 w-4 text-sky" />
              {project.city}
            </p>
          </div>
        </div>
      </section>

      <section className="px-5 py-16 sm:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl">
          <img
            src={project.cover}
            alt={project.title}
            className="h-[300px] w-full rounded-3xl object-cover shadow-float-lg sm:h-[450px]"
          />

          <div className="mt-12 grid gap-12 lg:grid-cols-[1fr_320px]">
            <div>
              <h2 className="font-serif text-3xl font-semibold text-navy">
                About the Project
              </h2>

              <p className="mt-5 text-base leading-8 text-slate-600">
                {project.description}
              </p>

              {project.address && (
                <p className="mt-4 text-sm font-medium text-slate-500">
                  <span className="font-semibold text-navy">Address:</span>{" "}
                  {project.address}
                </p>
              )}

              {project.roomTypes && (
                <div className="mt-8">
                  <h3 className="font-serif text-2xl font-semibold text-navy">
                    Room Types
                  </h3>

                  <ul className="mt-4 space-y-2 text-sm leading-7 text-slate-600">
                    {project.roomTypes.map((room) => (
                      <li key={room}>• {room}</li>
                    ))}
                  </ul>
                </div>
              )}

              {project.gallery.length > 0 && (
                <>
                  <h2 className="mt-14 font-serif text-3xl font-semibold text-navy">
                    Project Gallery
                  </h2>

                  <div className="mt-6 grid gap-5 sm:grid-cols-2">
                    {project.gallery.map((image, index) => (
                      <img
                        key={image}
                        src={image}
                        alt={`${project.title} ${index + 1}`}
                        className="h-64 w-full rounded-2xl object-cover"
                        loading="lazy"
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <aside className="h-fit rounded-2xl border border-line bg-slate-50 p-6">
              <h3 className="font-serif text-xl font-semibold text-navy">
                Services
              </h3>

              <p className="mt-4 text-sm leading-7 text-slate-600">
                {project.services}
              </p>

              <a
                href={project.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 block w-full rounded-full bg-navy px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-ocean"
              >
                View on Booking.com →
              </a>

              <div className="mt-6 border-t border-line pt-6 text-center">
                <p className="text-sm leading-6 text-slate-600">
                  Looking for a better direct-booking rate?
                  <br />
                  Message us on WhatsApp to check today's available offer.
                </p>

                <a
                  href="https://wa.me/923317736808"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 block w-full rounded-full bg-green-600 px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-green-700"
                >
                  💬 Message on WhatsApp
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
